import React from "react";
import { useQuery } from "@tanstack/react-query";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { Dialog, DialogContent, DialogTitle } from "../common/Dialog";
import { fetchTreatmentStats } from "../../apis/api.inventory";
import { queryInventoryTreatmentStatsKey } from "../../apis/queryKeys";
import useBrandTranslation from "../../hooks/useBrandTranslation";
import { formatMoney } from "../../utils/inventoryFormat";
import Spinner from "../common/Spinner";

// Estadísticas de uso de un tratamiento (cuánto lo usa el profesional/institución).
export default function TreatmentStatsDialog({
  treatment,
  ownerId,
  decimalSeparator,
  currencySymbol,
  onClose,
}) {
  const { t } = useBrandTranslation();
  const open = !!treatment;

  const { data, isLoading } = useQuery({
    queryKey: queryInventoryTreatmentStatsKey(treatment?.id),
    queryFn: () => fetchTreatmentStats(treatment.id, ownerId),
    enabled: open,
  });

  return (
    <Dialog open={open} onOpenChange={(o) => (!o ? onClose() : null)}>
      <DialogContent className="w-[94vw] max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogTitle className="text-lg font-semibold text-gray-900 mb-3">
          {t("inventory.stats.title", "Estadísticas de {{name}}", { name: treatment?.name || "" })}
        </DialogTitle>

        {isLoading ? (
          <Spinner className="w-8 h-8 mx-auto my-6" />
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="rounded-lg bg-green-50 border border-green-200 p-3">
                <p className="text-2xl font-bold text-green-700">{data?.timesUsed ?? 0}</p>
                <p className="text-xs text-gray-500">{t("inventory.stats.timesUsed", "Veces utilizado")}</p>
              </div>
              <div className="rounded-lg bg-blue-50 border border-blue-200 p-3">
                <p className="text-sm font-bold text-blue-700">
                  {formatMoney(data?.totalCost ?? 0, currencySymbol, decimalSeparator)}
                </p>
                <p className="text-xs text-gray-500">{t("inventory.stats.totalCost", "Costo acumulado")}</p>
              </div>
              <div className="rounded-lg bg-gray-50 border border-gray-200 p-3">
                <p className="text-xs font-semibold text-gray-700">
                  {data?.lastUsedAt
                    ? new Date(data.lastUsedAt).toLocaleDateString()
                    : t("inventory.stats.never", "Nunca")}
                </p>
                <p className="text-xs text-gray-500">{t("inventory.stats.lastUsed", "Último uso")}</p>
              </div>
            </div>

            {data?.monthlyUsage && data.monthlyUsage.length > 0 ? (
              <div>
                <p className="text-sm font-semibold text-gray-700 mb-2">
                  {t("inventory.stats.monthlyUsage", "Uso por mes")}
                </p>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={data.monthlyUsage}>
                    <XAxis dataKey="month" fontSize={11} />
                    <YAxis allowDecimals={false} fontSize={11} />
                    <Tooltip />
                    <Bar dataKey="count" fill="#16a34a" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <p className="text-sm text-gray-500 text-center py-4">
                {t("inventory.stats.empty", "Este tratamiento aún no se utilizó")}
              </p>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
